import { CreditsPlusIcon } from '@components/Icons/CreditsPlusIcon';
import { formatCreditsWithUnit, formatDurationToString } from '@lib/utils/units';
import { billingStore } from '@stores/Billing';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { formatDistance } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import css from './CreditsBreakdown.module.scss';

/**
 * The credits detail panel: plan badge, total credits, media-processing + storage
 * remaining, last-updated, and (when `showActions`) the Billing history + Upgrade
 * links. Extracted from CreditsBox so the nav account footer popover and the
 * credits hover card render the exact same breakdown.
 *
 * `showActions` gates the billing/upgrade links — they belong on the panel that
 * opens from the bottom of the nav, not e.g. an inline hover anchored below.
 */
export const CreditsBreakdown = observer(
  ({
    showActions = true,
    embedded = false,
    source,
    onNavigate,
  }: {
    showActions?: boolean;
    embedded?: boolean;
    source: string;
    /** Called just before an action link navigates — lets a parent popover close
     *  itself first (the hover-card caller can omit it; it dismisses on its own). */
    onNavigate?: () => void;
  }) => {
    const navigateTo = useNavigate();
    const credits = billingStore.creditsLeft;
    if (credits === null) return null;

    const ingestLeftFormatted = formatDurationToString(billingStore.ingestLeft ?? 0, {
      precision: 0,
    });

    return (
      <div className={classNames(css.dropdown, embedded && css.embedded)}>
        <div className={css.topRow}>
          <span className={css.badge}>
            {billingStore.data?.subscription?.product?.name || 'No plan'}
          </span>
          <div className={css.totalCredits}>
            <CreditsPlusIcon />
            <span>{formatCreditsWithUnit(credits, 'down')}</span>
          </div>
        </div>
        <div className={classNames(css.section, css.flexDirectionColumn)}>
          <div className={css.flexDirectionRow}>
            <div className={css.itemPrimary}>Media processing</div>
            <div className={css.itemSecondary}>≈ {ingestLeftFormatted} left</div>
          </div>
          <div className={css.flexDirectionRow}>
            <div className={css.itemPrimary}>Storage</div>
            <div className={css.itemSecondary}>
              = {formatDurationToString(billingStore.storageLeft, { precision: 0, detail: 2 })} left
            </div>
          </div>
          <div className={classNames(css.flexDirectionRow, css.lastUpdated)}>
            <div className={css.itemPrimary}>Last updated</div>
            <div className={css.itemSecondary}>
              {billingStore.lastUpdate
                ? formatDistance(billingStore.lastUpdate, new Date(), { addSuffix: true })
                : 'Never'}
            </div>
          </div>
        </div>
        {showActions && (
          <>
            <div className={css.sectionLink}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  trackingStore.navItemClicked('Billing & Credits History', '/settings/billing', {
                    source,
                  });
                  onNavigate?.();
                  navigateTo('/settings/billing');
                }}
                className={css.itemPrimary}
              >
                Billing &amp; Credits History <span className={css.externalArrow}>↗</span>
              </a>
            </div>
            <div className={css.bottomRow}>
              <span className={css.muted}>Need more?</span>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  trackingStore.upgradeClicked({ source });
                  onNavigate?.();
                  navigateTo('/pricing');
                }}
                className={css.upgrade}
              >
                Upgrade plan <span className={css.externalArrow}>↗</span>
              </a>
            </div>
          </>
        )}
      </div>
    );
  },
);
